/**
 * Terrain camera-floor selection (gap G20 — feature-flagged ElevationLayer).
 *
 * A true-3D Giro3D `ElevationLayer` raises the terrain surface to the DEM's
 * real elevation (often hundreds of metres above the ellipsoid). The original
 * fly-to logic assumed a z=0 ground plane, which placed the fly-to target — and
 * frequently the camera itself — beneath the mesh, leaving the user buried (the
 * "flash then disappear" instability the spike root-caused).
 *
 * This pure helper isolates the floor-selection decision from the Giro3D
 * adapter so it can be unit-tested without instantiating a WebGL scene. When the
 * elevation-mesh flag is OFF it always returns `null`, so the stable flat-terrain
 * camera behaviour is byte-for-byte unchanged.
 */

/** Minimal shape of a Giro3D layer record needed to resolve the terrain floor. */
export interface TerrainFloorLayerRecord {
  readonly descriptor: { readonly kind: string };
  readonly entity?: unknown;
}

/**
 * Return the mounted DEM's real ground floor (`minmax.min`, EPSG:3857 metres)
 * from the first `terrain-source` `ElevationLayer` with a finite min, or `null`
 * when the flag is off or no such layer is mounted.
 *
 * @param records       Iterable of layer records (e.g. `adapter.layers.values()`).
 * @param flagEnabled   The `TERRAIN_ELEVATION_MESH` feature-flag value.
 */
export function selectTerrainElevationFloor(
  records: Iterable<TerrainFloorLayerRecord>,
  flagEnabled: boolean,
): number | null {
  if (!flagEnabled) return null;
  for (const record of records) {
    if (record.descriptor.kind !== 'terrain-source') continue;
    const mm = (record.entity as { minmax?: { min?: number } } | undefined)?.minmax;
    if (mm && typeof mm.min === 'number' && Number.isFinite(mm.min)) {
      return mm.min;
    }
  }
  return null;
}
